import { Sentence, sentenceDTO } from './data-model.sentence';
import { Tokens } from './data-model.tokens';
import { editStateEnumType } from './data-model.enums';
import { CapicheLoggerCore } from '@capiche/ts-logger-core';

import { SENTENCE_TERMINATORS, SPECIAL_PATTERNS, createAbbreviationRe } from './data-model.regexp';

const logger: CapicheLoggerCore = new CapicheLoggerCore('sentences:', false);

export class Sentences {
  constructor(public readonly sentences: Sentence[]) {
    logger.log('constructor:', sentences);
  }

  static fromString(text: string, from: number = 0, srcId?: string, seqId?: number): Sentences {
    logger.log('fromString:', text, seqId, srcId);
    const splitSentences: stringFragments = fragmentString(text);

    const sentences = splitSentences.map(({ text: sentenceText, index }) => {
      // Offset index by paragraph `from` if necessary
      return Sentence.fromString(sentenceText, from + index, seqId, srcId);
    });

    return new Sentences(sentences);
  }

  // Factory methods
  static fromDTOs(dtos: sentenceDTO[]): Sentences {
    return new Sentences(dtos.map((dto) => Sentence.fromDTO(dto)));
  }

  // Serialization
  toDTOs(): sentenceDTO[] {
    return this.sentences.map((sentence) => sentence.toDTO());
  }

  // Collection operations
  filter(predicate: (sentence: Sentence) => boolean): Sentences {
    return new Sentences(this.sentences.filter(predicate));
  }

  map(fn: (sentence: Sentence) => Sentence): Sentences {
    return new Sentences(this.sentences.map(fn));
  }

  // Business logic
  filterOnEditState(retainStates: editStateEnumType[]): Sentences {
    return this.filter((s) => retainStates.includes(s.dto.editState)).map(
      (s) =>
        new Sentence({
          ...s.dto,
          tokens: Tokens.fromDTOs(s.dto.tokens).filterOnEditState(retainStates).toDTOs(),
        }),
    );
  }

  getCorrectedSentences(): Sentences {
    return this.filter((sentence) => sentence.isCorrected());
  }

  getSentencesWithMisspelledTokens(): Sentences {
    return this.filter((sentence) => sentence.hasMisspelledTokens());
  }

  getTotalTokenCount(): number {
    return this.sentences.reduce((total, sentence) => total + sentence.getTokenCount(), 0);
  }

  getTotalMisspelledTokenCount(): number {
    return this.sentences.reduce((total, sentence) => total + sentence.getMisspelledTokenCount(), 0);
  }

  count(): number {
    return this.sentences.length;
  }
}

// interface to hold text fragments with their original index
export interface stringFragment {
  text: string;
  index: number;
}
// type alias for an array of text fragments
type stringFragments = stringFragment[];

/**
 * Splits input text into sentence fragments while preserving absolute indexes.
 *
 * Approach:
 *  1. Mask abbreviations, decimals, and all SPECIAL_PATTERNS (URLs, emails, ellipses, etc.)
 *     by replacing them with spaces of equal length so indexes remain valid.
 *  2. Find sentence boundaries in the masked text by scanning for SENTENCE_TERMINATORS.
 *     (Ellipses and masked regions never trigger splits.)
 *  3. Slice the original text using these boundary positions and trim leading/trailing spaces.
 *  4. Return an array of { text, index } fragments for downstream Sentence construction.
 *
 * Notes:
 *  - Punctuation remains attached to the preceding sentence.
 *  - No reliance on capitalization or grammar conventions.
 *  - Designed for imperfect writing where spacing and punctuation are inconsistent.
 */
export function fragmentString(text: string): stringFragment[] {
  const t0 = performance.now();
  const fragments: stringFragment[] = [];
  let masked = text;

  const masks: { start: number; end: number }[] = [];

  logger.log('fragmentString: text:', text);

  // 1. Mask abbreviations, specials, decimals
  const allMasks = [
    // ensure global flag
    ...text.matchAll(/\([^()]*\)/g), // ignore ( )
    ...text.matchAll(/\[[^\[\]]*\]/g), // ignore [ ]
    ...text.matchAll(/\{[^{}]*\}/g), // ignore { }
    ...text.matchAll(createAbbreviationRe()),
    ...text.matchAll(/\d+\.\d+/g),
    ...SPECIAL_PATTERNS.flatMap((re) =>
      Array.from(text.matchAll(new RegExp(re.source, re.flags.includes('g') ? re.flags : re.flags + 'g'))),
    ),
  ];

  //logger.log(
  //  'allMasks:',
  //  allMasks.map((m) => {
  //    return { match: m[0], index: m.index };
  //  }),
  //);

  for (const m of allMasks) {
    const start = m.index!;
    const end = start + m[0].length;
    masks.push({ start, end });
    masked = masked.slice(0, start) + ' '.repeat(m[0].length) + masked.slice(end);
  }

  // 2. Find sentence boundaries on the masked string
  const boundaryRe = new RegExp(`[${SENTENCE_TERMINATORS}]`, 'gu');

  let lastIndex = 0;

  for (const match of masked.matchAll(boundaryRe)) {
    //logger.log('fragmentString: match:', match[0], 'at', match.index, match);
    const end = match.index! + match[0].length;
    const raw = text.slice(lastIndex, end);

    // ✅ adjust index to account for trimmed leading whitespace
    const leading = raw.match(/^\s+/);
    const offset = leading ? leading[0].length : 0;

    const fragmentText = raw.trim();
    const fragmentIndex = lastIndex + offset;

    logger.log('fragmentString: fragment:', `"${fragmentText}"`, 'from', fragmentIndex, 'to', end);
    if (fragmentText) fragments.push({ text: fragmentText, index: fragmentIndex });
    lastIndex = end;
  }

  // 3. Remainder
  if (lastIndex < text.length) {
    const rawRest = text.slice(lastIndex);
    const leading = rawRest.match(/^\s+/);
    const offset = leading ? leading[0].length : 0;
    const restText = rawRest.trim();
    if (restText) fragments.push({ text: restText, index: lastIndex + offset });
  }

  const t1 = performance.now();
  logger.log(`fragmentString: processed in ${(t1 - t0).toFixed(2)} ms`);
  return fragments;
}
